import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const documentErrorTypeValues = [
  "UNKNOWN",
  "UNKNOWN_ZIP_ERROR",
  "FILE_DOWNLOAD_FAILED",
  "FILE_NOT_FOUND",
  "FILE_ANNOTATION_NOT_FOUND",
  "FAILED_TO_PARSE_FILE_NAME",
  "BATCH_FILE_UPLOAD_FAILED",
  "BATCH_FILE_TAG_FAILED",
  "FILE_BUFFER_NOT_FOUND",
  "FILE_ZIP_FAILED",
  "FILE_TOO_LARGE_FOR_ZIP_PART",
  "ACTIVE_HIC_NOT_FOUND",
  "HIC_NOT_ATTACHABLE",
  "HIC_TOTAL_DOES_NOT_MATCH_QUOTE",
  "HIC_NOT_SIGNED",
  "HIC_SIGNED_DATE_MISSING",
  "HIC_FILE_MISSING",
  "CHANGE_ORDER_ADDENDUM_CO_SIGNER_UNSUPPORTED",
  "ACTIVE_HIC_FILE_MISSING",
  "CERTIFICATE_OF_COMPLETION_FILE_MISSING",
  "UNAUTHORIZED_FILE_CALLER",
  "ACTIVE_HIC_ASSOCIATION_FAILED",
  "SIGNED_URL_BATCH_FAILED",
  "INVALID_FILE_CREATE_INPUT",
  "ORPHAN_SWEEP_ABSENT_RATIO_EXCEEDED",
  "PDF_UNREADABLE",
] as const;
const documentErrorTypeSchema = z.enum(documentErrorTypeValues);
const DocumentErrorType = documentErrorTypeSchema.enum;
type DocumentErrorType = z.infer<typeof documentErrorTypeSchema>;

export type DocumentErrors =
  | { errorType: typeof DocumentErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof DocumentErrorType.UNKNOWN_ZIP_ERROR; data: undefined }
  | { errorType: typeof DocumentErrorType.FILE_DOWNLOAD_FAILED; data: { url: string } }
  | { errorType: typeof DocumentErrorType.FILE_NOT_FOUND; data: { fileId: string } }
  | { errorType: typeof DocumentErrorType.FILE_ANNOTATION_NOT_FOUND; data: { id: string } }
  | { errorType: typeof DocumentErrorType.FAILED_TO_PARSE_FILE_NAME; data: { url: string } }
  | { errorType: typeof DocumentErrorType.BATCH_FILE_UPLOAD_FAILED; data: { fileNames: string[] } }
  | { errorType: typeof DocumentErrorType.BATCH_FILE_TAG_FAILED; data: { fileIds: string[] } }
  | { errorType: typeof DocumentErrorType.FILE_ZIP_FAILED; data: { fileIds: string[] } }
  | { errorType: typeof DocumentErrorType.FILE_BUFFER_NOT_FOUND; data: { fileId: string } }
  | {
      errorType: typeof DocumentErrorType.FILE_TOO_LARGE_FOR_ZIP_PART;
      data: { fileName: string; rawBytes: number; compressedBytes: number; maxBytes: number };
    }
  | { errorType: typeof DocumentErrorType.ACTIVE_HIC_NOT_FOUND; data: { quoteVersionId: string } }
  | { errorType: typeof DocumentErrorType.HIC_NOT_ATTACHABLE; data: { documentId: string; status: string } }
  | {
      errorType: typeof DocumentErrorType.HIC_TOTAL_DOES_NOT_MATCH_QUOTE;
      data: {
        documentId: string;
        quoteVersionId: string;
        hicAmount: number;
        quoteHicTotal: number;
        submittedAmount: number;
      };
    }
  | { errorType: typeof DocumentErrorType.HIC_NOT_SIGNED; data: { quoteVersionId: string; documentId: string } }
  | { errorType: typeof DocumentErrorType.HIC_SIGNED_DATE_MISSING; data: { bidId: number } }
  | { errorType: typeof DocumentErrorType.HIC_FILE_MISSING; data: { bidId: number } }
  | {
      errorType: typeof DocumentErrorType.CHANGE_ORDER_ADDENDUM_CO_SIGNER_UNSUPPORTED;
      data: { quoteVersionId: string };
    }
  | { errorType: typeof DocumentErrorType.ACTIVE_HIC_FILE_MISSING; data: { quoteId: string; documentId: string } }
  | { errorType: typeof DocumentErrorType.CERTIFICATE_OF_COMPLETION_FILE_MISSING; data: { bidId: number } }
  | { errorType: typeof DocumentErrorType.UNAUTHORIZED_FILE_CALLER; data: { callerOrgId: string } }
  | {
      errorType: typeof DocumentErrorType.ACTIVE_HIC_ASSOCIATION_FAILED;
      data: { documentId: string; quoteId: string };
    }
  | { errorType: typeof DocumentErrorType.SIGNED_URL_BATCH_FAILED; data: { fileCount: number; cause: string } }
  | { errorType: typeof DocumentErrorType.INVALID_FILE_CREATE_INPUT; data: { issues: string } }
  | {
      errorType: typeof DocumentErrorType.ORPHAN_SWEEP_ABSENT_RATIO_EXCEEDED;
      data: { absent: number; checked: number; bail: number };
    }
  | { errorType: typeof DocumentErrorType.PDF_UNREADABLE; data: { fileId: string } };

export const documentErrorDetails: Record<DocumentErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown document error" },
  UNKNOWN_ZIP_ERROR: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Unknown zip creation error",
  },
  FILE_DOWNLOAD_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to download file from external URL",
  },
  FILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File not found",
  },
  FILE_ANNOTATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File annotation not found",
  },
  FAILED_TO_PARSE_FILE_NAME: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Failed to parse file name from URL",
  },
  BATCH_FILE_UPLOAD_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to upload batch of files",
  },
  BATCH_FILE_TAG_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to tag batch of files",
  },
  FILE_BUFFER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File buffer not found",
  },
  FILE_ZIP_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to download batch of files",
  },
  FILE_TOO_LARGE_FOR_ZIP_PART: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "File exceeds Sunrun zip-part size limit after compression; manual review required",
  },
  ACTIVE_HIC_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Active HIC not found",
  },
  HIC_NOT_ATTACHABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC document is not in an attachable status",
  },
  HIC_TOTAL_DOES_NOT_MATCH_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "HIC total does not match the quote version it would be attached to",
  },
  HIC_NOT_SIGNED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC not signed",
  },
  HIC_SIGNED_DATE_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC signed date missing",
  },
  HIC_FILE_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC file missing",
  },
  CHANGE_ORDER_ADDENDUM_CO_SIGNER_UNSUPPORTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Change order addendum requested for a homeowner with a co-signer",
  },
  ACTIVE_HIC_FILE_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Active HIC is signed but its file is missing",
  },
  CERTIFICATE_OF_COMPLETION_FILE_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Certificate of completion file missing",
  },
  UNAUTHORIZED_FILE_CALLER: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller is not authorized to record a delivery for this file",
  },
  ACTIVE_HIC_ASSOCIATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to associate the active HIC with its quote; neither write branch completed",
  },
  SIGNED_URL_BATCH_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to fetch files for MCP signed-URL batch",
  },
  INVALID_FILE_CREATE_INPUT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "createFileAndUploadToS3 received input that failed schema validation",
  },
  ORPHAN_SWEEP_ABSENT_RATIO_EXCEEDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Most orphan sweep candidates read as absent in S3, which indicates a misconfiguration",
  },
  PDF_UNREADABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "PDF is malformed or truncated and could not be parsed",
  },
};
